/**
 * TASK PANEL STORE - v2.0
 * 
 * Store Zustand para gerenciar a janela flutuante de tarefas
 * Controla apenas estado de UI (aberto/fechado, posição, tamanho, pin)
 * Os dados das tarefas continuam no taskStore existente
 * 
 * v2.0: Adicionado isPinned para fixar posição da janela
 */

import { create } from 'zustand';
import { logger } from '../utils/logger';
import { safeSetItem } from '../utils/safeLocalStorage';

interface TaskPanelState {
  // UI State
  isOpen: boolean;
  isPinned: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  
  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  togglePin: () => void;
  setPosition: (x: number, y: number) => void;
  setSize: (width: number, height: number) => void;
}

// Chave para localStorage
const TASK_PANEL_UI_KEY = 'bigwa-task-panel-ui';

// Posição default
const DEFAULT_POSITION = () => ({
  x: Math.max(100, window.innerWidth - 450),
  y: 80
});

const DEFAULT_SIZE = { width: 400, height: 500 };

// Carregar configurações de UI do localStorage
function loadUIState() {
  try {
    const saved = localStorage.getItem(TASK_PANEL_UI_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        position: parsed.position || DEFAULT_POSITION(),
        size: parsed.size || DEFAULT_SIZE,
        isPinned: parsed.isPinned || false
      };
    }
  } catch (e) {
    logger.error('TaskPanel', 'Erro ao carregar UI:', e);
  }
  return {
    position: DEFAULT_POSITION(),
    size: DEFAULT_SIZE,
    isPinned: false
  };
}

// Salvar configurações de UI no localStorage
function saveUIState(
  position: { x: number; y: number }, 
  size: { width: number; height: number },
  isPinned: boolean
) {
  try {
    safeSetItem(TASK_PANEL_UI_KEY, JSON.stringify({ position, size, isPinned }));
  } catch (e) {
    logger.error('TaskPanel', 'Erro ao salvar UI:', e);
  }
}

export const useTaskPanelStore = create<TaskPanelState>((set, get) => {
  const uiState = loadUIState();
  
  return {
    // Initial State
    isOpen: false,
    isPinned: uiState.isPinned,
    position: uiState.position,
    size: uiState.size,
    
    // Actions
    open: () => {
      const { isPinned } = get();
      if (isPinned) {
        // Se pinned, abre na posição salva
        set({ isOpen: true });
      } else {
        // Se não pinned, abre na posição default
        set({ 
          isOpen: true,
          position: DEFAULT_POSITION()
        });
      }
    },
    
    close: () => set({ isOpen: false }),
    
    toggle: () => {
      const { isOpen } = get();
      if (isOpen) {
        get().close();
      } else {
        get().open();
      }
    },
    
    togglePin: () => {
      const { isPinned, position, size } = get();
      const newPinned = !isPinned;
      set({ isPinned: newPinned });
      saveUIState(position, size, newPinned);
    },
    
    setPosition: (x, y) => {
      const { size, isPinned } = get();
      set({ position: { x, y } });
      saveUIState({ x, y }, size, isPinned);
    },
    
    setSize: (width, height) => {
      const { position, isPinned } = get();
      set({ size: { width, height } });
      saveUIState(position, { width, height }, isPinned);
    },
  };
});

export default useTaskPanelStore;
